<template>
  <div class="add-letters">
    <div class="add-letters__content">
      <div v-for="(input, index) in textBoxes" :key="index" class="add-letters__name-wrapper">
        <div class="add-letters__input-wrapper">
          <input
            :id="`textbox-${index}`"
            v-model.trim="input.value"
            type="text"
            :class="inputStyles(index, input)"
            @focus="handleFocus(index)"
            @blur="handleBlur(index)"
            @input="handleInput(index, $event)"
            @keydown.enter.prevent="addTextBox"
            @keyup="triggerChange(index, $event)"
          />
          <div v-if="letterPreviewMessages[index]" class="add-letters__error">
            {{ letterPreviewMessages[index] }}
          </div>
        </div>
        <button
          type="button"
          class="add-letters__button add-letters__button--remove"
          @click="removeTextBox(index)"
        >
          <UseSvg icon="modal-close" class="add-letters__remove-icon" />
        </button>
      </div>

      <div class="add-letters__new-name">
        <button type="button" class="add-letters__button" @click="addTextBox">
          + Add another name
        </button>
      </div>
    </div>
    <div class="add-letters__content">
      <Preview />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import Preview from "components/jiffy/vue-product-details-dtf/Letters/Preview.vue";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { useDtfStore } from "../store";

const store = useDtfStore();
const letterNumberArtwork = computed(() => store.letterNumberArtwork);
const letterPreviewMessages = computed(() => store.letterPreviewMessages);
const textBoxes = ref([{ value: "" }]);
const activeTextbox = ref(null);
let timer = null;

const inputStyles = (index, input) => ({
  "add-letters__input": true,
  "add-letters__input--selected": letterNumberArtwork.value.textIndex === index && !!input.value
});

const setArtworkDefaultValues = () => {
  store.updateArtworkValues({ index: 0, textIndex: 0, texts: [] });
  store.generateLettersPreview();
};

const handleChange = (index, event) => {
  const { value } = event.target;
  activeTextbox.value = index;
  const textIndex = index;

  if (!value) {
    store.setLetterPreviewMessage({ textIndex, message: null });
  }

  const texts = textBoxes.value.map(obj => obj.value);
  const quantity = texts.filter(Boolean).length;

  store.updateArtworkValues({ index: 0, textIndex, texts, quantity });

  if (value) {
    store.generateLettersPreview();
  }
};

const triggerChange = (index, event) => {
  clearTimeout(timer);

  timer = setTimeout(() => {
    const { value } = event.target;

    if (value) {
      store.setLetterPreviewLoading(true);
    }

    handleChange(index, event);
  }, 500); // 500ms = 0.5 seconds
};

const handleInput = (index, event) => {
  const value = event.target.value.trim();
  if (value !== "") {
    textBoxes.value[index].value = value;
  }
};

const removeTextBox = index => {
  if (textBoxes.value.length === 1) {
    textBoxes.value = [{ value: "" }];
    store.clearLetterPreviewMessages();
    setArtworkDefaultValues();
    return;
  }

  store.removeLetterPreviewMessage(index);

  textBoxes.value.splice(index, 1);

  let textIndex = index;
  const currentIndex = letterNumberArtwork.value.textIndex;
  let texts = textBoxes.value.map(obj => obj.value);

  // Remove textboxes above, empty or not
  if (textIndex < currentIndex) {
    textIndex = currentIndex - 1;
  }

  if (!texts[textIndex]) {
    const reversedTexts = [...texts].reverse();
    textIndex = texts.length - 1 - reversedTexts.findIndex(Boolean);
  }

  if (textBoxes.value.length === 1 && !textBoxes.value[0].value) {
    texts = [];
  }

  const quantity = texts.filter(Boolean).length;
  store.updateArtworkValues({ index: 0, textIndex, texts, quantity });
  store.generateLettersPreview();
};

const handleFocus = index => {
  const textBox = textBoxes.value[index];
  activeTextbox.value = index;
  if (!textBox.value) return;

  store.updateArtworkValues({ index: 0, textIndex: index });
  store.generateLettersPreview();
};

const addTextBox = () => {
  const currentIndex = textBoxes.value.length - 1;
  if (!textBoxes.value[currentIndex].value) return;

  textBoxes.value.push({ value: "" });
  const texts = textBoxes.value.map(obj => obj.value);

  store.updateArtworkValues({ index: 0, textIndex: currentIndex, texts });
  store.generateLettersPreview({ useDebounce: false });

  nextTick(() => {
    const newIndex = textBoxes.value.length - 1;
    const newTextbox = document.getElementById(`textbox-${newIndex}`);
    if (newTextbox) {
      newTextbox.focus();
    }
  });
};

const handleBlur = index => {
  activeTextbox.value = index;
  const texts = textBoxes.value.map(obj => obj.value);
  const quantity = texts.filter(Boolean).length;
  store.updateArtworkValues({ index: 0, texts, quantity });
};

watch(
  () => letterNumberArtwork.value.style,
  (newValue, oldValue) => {
    if (oldValue && newValue === null) {
      textBoxes.value = [{ value: "" }];
      store.clearLetterPreviewMessages();
    }
  }
);
</script>

<style lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";
@import "frontend/init/jiffy/button/mixin.scss";

.add-letters {
  display: flex;
  gap: 16px;
  flex-direction: column;

  @include with-large-desktop {
    gap: 24px;
    flex-direction: row;
  }

  &__content {
    flex: 1;
    margin-bottom: 16px;

    @include with-large-desktop {
      flex: 1 1 50%;
    }
  }

  &__input {
    padding: 12px 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $text-black;
    font-size: 16px;
    line-height: 20px;
    border-radius: 4px;
    border: 2px solid $gray-border;
    background: $white;
    width: 100%;

    &--selected {
      border-color: $primary-600;
    }
  }

  &__button {
    @include clear-button;

    color: $deep-blue;
    font-weight: 700;
    margin-left: 8px;
    text-decoration: underline;

    &--remove {
      text-decoration: none;
      color: $warm-grey;
    }
  }

  &__remove-icon {
    width: 16px;
    height: 16px;
  }

  &__new-name {
    margin-top: 16px;
  }

  &__name-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__input-wrapper {
    flex: 1 1 90%;
  }

  &__error {
    color: $red-light;
    padding: 2px;
    font-size: 13px;

    @include with-large-desktop {
      font-size: 14px;
    }
  }
}
</style>
